import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useApprovalsAwaiting } from '../../features/time/approvals-badge';
// The hook file, not the feature's index — the index would pull the page into
// the shell's chunk.
import { useActivityAccess } from '../../features/activity/api';
import { NAV_SECTIONS } from './nav';

/**
 * A nav item may carry a count of work waiting for THIS viewer. Only /time has
 * one today (timesheets submitted for your approval); it is zero — and renders
 * nothing — for anyone the server has not made an approver.
 */
function useNavCounts(): Record<string, number> {
  const awaitingApproval = useApprovalsAwaiting();
  return { '/time': awaitingApproval };
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const counts = useNavCounts();
  const isAdmin = useActivityAccess();
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4" aria-label="Primary">
      {NAV_SECTIONS.map((section, i) => (
        <div key={section.label ?? `section-${i}`}>
          {section.label && (
            <p className="mb-1 px-3 text-caption font-bold uppercase tracking-wider text-grey-400">
              {section.label}
            </p>
          )}
          <ul className="space-y-0.5">
            {section.items.filter((item) => !item.adminOnly || isAdmin).map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
                      isActive
                        ? 'bg-secondary text-white shadow-card'
                        : 'text-secondary-600 hover:bg-grey-100 hover:text-secondary',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {(counts[item.to] ?? 0) > 0 && (
                        <span
                          className={cn(
                            'num ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-caption font-bold leading-none',
                            isActive ? 'bg-white/20 text-white' : 'bg-accent text-white',
                          )}
                          title={`${counts[item.to]} timesheet${counts[item.to] === 1 ? '' : 's'} waiting for your approval`}
                        >
                          {counts[item.to]}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function Brand({ bordered = true }: { bordered?: boolean }) {
  return (
    <div
      className={cn('flex h-16 items-center gap-2.5 px-5', bordered && 'border-b border-grey-200')}
    >
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary font-serif text-sm font-bold text-white">
        $
      </span>
      <div className="leading-tight">
        <p className="text-sm font-black text-secondary">Solid Financials</p>
        <p className="font-serif text-caption italic text-grey-500">by Solid Digital</p>
      </div>
    </div>
  );
}

export interface SidebarProps {
  /** Mobile drawer open state (collapsed on mobile by default). */
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/**
 * Secondary sidebar — sits to the RIGHT of the 64px hub app rail. The rail
 * script auto-offsets <body> (64px left on desktop, 56px top below 1024px),
 * so this component lives in normal flow and never adds its own rail offset.
 */
export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  return (
    <>
      {/* Desktop: static column */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-grey-200 bg-white lg:flex">
        <Brand />
        <NavList />
      </aside>

      {/* Mobile: overlay drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-secondary-950/50 backdrop-blur-sm"
            onClick={onMobileClose}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 animate-fade-in flex-col bg-white shadow-elevated">
            <div className="flex items-center justify-between border-b border-grey-200 pr-3">
              <Brand bordered={false} />
              <button
                type="button"
                onClick={onMobileClose}
                className="rounded-lg p-2 text-grey-500 hover:bg-grey-100 hover:text-secondary"
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <NavList onNavigate={onMobileClose} />
          </aside>
        </div>
      )}
    </>
  );
}
